import React, { useRef, useState } from "react";
import { UploadCloud, Film, X, Video, Layers, ListVideo, Plus, Scissors } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const ANGLES = ["Side-on", "Front-on", "45-degree", "Behind-bowler"];
const HANDS = ["Right-Handed", "Left-Handed"];

let clipSeq = 0;

export const VideoUploadZone = ({ onAnalyze, disabled }) => {
  const inputRef = useRef(null);
  const [clips, setClips] = useState([]); // {key, file, url, angle}
  const [dragging, setDragging] = useState(false);
  const [batterName, setBatterName] = useState("");
  const [hand, setHand] = useState("Right-Handed");
  const [mode, setMode] = useState("separate"); // multi_angle | separate
  const [sessionSplit, setSessionSplit] = useState(false);

  const addFiles = (fileList) => {
    const vids = Array.from(fileList || []).filter((f) => f.type.startsWith("video/"));
    if (!vids.length) return;
    setClips((prev) => [
      ...prev,
      ...vids.map((file, i) => ({
        key: ++clipSeq,
        file,
        url: URL.createObjectURL(file),
        angle: ANGLES[Math.min(i, ANGLES.length - 1)] || "Side-on",
      })),
    ]);
  };

  const removeClip = (key) => {
    setClips((prev) => {
      const c = prev.find((x) => x.key === key);
      if (c) URL.revokeObjectURL(c.url);
      return prev.filter((x) => x.key !== key);
    });
  };

  const setAngle = (key, angle) =>
    setClips((prev) => prev.map((c) => (c.key === key ? { ...c, angle } : c)));

  const submit = () => {
    if (!clips.length) return;
    if (clips.length === 1 && sessionSplit) {
      onAnalyze({
        files: [{ file: clips[0].file, angle: clips[0].angle }],
        batterName: batterName.trim() || "Net Batter",
        hand,
        mode: "session",
      });
      return;
    }
    const effMode = clips.length > 1 ? mode : "separate";
    if (effMode === "multi_angle") {
      const uniq = new Set(clips.map((c) => c.angle));
      if (uniq.size < clips.length) {
        toast.warning("Set a distinct camera angle for each clip", {
          description: "Multi-angle mode works best when each clip is labelled with its own angle.",
        });
      }
    }
    onAnalyze({
      files: clips.map((c) => ({ file: c.file, angle: c.angle })),
      batterName: batterName.trim() || "Net Batter",
      hand,
      mode: effMode,
    });
  };

  const multi = clips.length > 1;

  return (
    <div data-testid="video-upload-zone" className="flex flex-col gap-5">
      <div
        data-testid="video-drop-area"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`relative overflow-hidden rounded-2xl border-2 border-dashed transition-all cursor-pointer ${
          dragging ? "border-emerald-400 bg-emerald-500/5" : "border-slate-700 bg-[#12181F] hover:border-emerald-500/50 hover:bg-[#141b23]"
        }`}
      >
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="h-16 w-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
            <UploadCloud className="h-8 w-8 text-emerald-400" />
          </div>
          <p className="font-heading font-bold text-lg text-slate-100">
            {clips.length ? "Add more clips" : "Drop your net clips here"}
          </p>
          <p className="text-sm text-slate-400 mt-1">or click to browse — MP4, MOV, WEBM · up to ~30s each</p>
          <p className="font-mono-tel text-[10px] tracking-[0.2em] text-slate-600 uppercase mt-4">
            Upload one clip, or several for multi-angle / multi-shot review
          </p>
        </div>
        <input
          ref={inputRef}
          data-testid="video-file-input"
          type="file"
          accept="video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {/* Selected clips */}
      {clips.length > 0 && (
        <div data-testid="selected-clips-list" className="flex flex-col gap-3">
          {clips.map((c, i) => (
            <div key={c.key} data-testid="selected-clip-row" className="rounded-xl border border-slate-800 bg-[#12181F] p-3 flex items-center gap-3">
              <video src={c.url} className="h-16 w-24 object-cover rounded-lg bg-black shrink-0" muted />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Film className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  <span className="text-sm text-slate-200 truncate">{c.file.name}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-mono-tel text-[9px] tracking-widest text-cyan-400 uppercase">Angle</span>
                  <select
                    data-testid={`clip-angle-select-${i}`}
                    value={c.angle}
                    onChange={(e) => setAngle(c.key, e.target.value)}
                    className="bg-[#0B0F12] border border-slate-700 rounded-md text-xs text-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
                  >
                    {ANGLES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                data-testid={`remove-clip-btn-${i}`}
                onClick={() => removeClip(c.key)}
                className="h-8 w-8 rounded-full bg-black/40 border border-slate-700 flex items-center justify-center text-slate-400 hover:bg-red-500/80 hover:text-white transition-colors shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            data-testid="add-more-clips-btn"
            onClick={() => inputRef.current?.click()}
            className="flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-slate-700 text-sm text-slate-400 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add another clip
          </button>
        </div>
      )}

      {/* Multi-clip question */}
      {multi && (
        <div data-testid="multi-video-mode-question" className="rounded-xl border border-cyan-500/30 bg-cyan-500/[0.04] p-4">
          <p className="text-sm font-semibold text-slate-100 mb-1">You've added {clips.length} clips — how should we treat them?</p>
          <p className="text-xs text-slate-400 mb-3">This changes whether you get one combined report or one report per shot.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid="mode-multi-angle-btn"
              onClick={() => setMode("multi_angle")}
              className={`text-left rounded-lg border p-3 transition-colors ${
                mode === "multi_angle" ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-700 bg-[#0B0F12] hover:border-slate-600"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Layers className={`h-4 w-4 ${mode === "multi_angle" ? "text-emerald-400" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-100">Same shot, different angles</span>
              </div>
              <p className="text-xs text-slate-400">Combine all clips into one consolidated report of a single shot.</p>
            </button>
            <button
              data-testid="mode-separate-btn"
              onClick={() => setMode("separate")}
              className={`text-left rounded-lg border p-3 transition-colors ${
                mode === "separate" ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-700 bg-[#0B0F12] hover:border-slate-600"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <ListVideo className={`h-4 w-4 ${mode === "separate" ? "text-emerald-400" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-100">Different shots to review</span>
              </div>
              <p className="text-xs text-slate-400">Get a separate report for each clip, reviewed independently.</p>
            </button>
          </div>
        </div>
      )}

      {/* Session split option (single clip only) */}
      {clips.length === 1 && (
        <button
          data-testid="session-split-toggle"
          onClick={() => setSessionSplit((v) => !v)}
          className={`text-left rounded-xl border p-4 transition-colors ${
            sessionSplit ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-700 bg-[#12181F] hover:border-slate-600"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center border ${sessionSplit ? "bg-emerald-500/15 border-emerald-500/40" : "bg-[#0B0F12] border-slate-700"}`}>
              <Scissors className={`h-4 w-4 ${sessionSplit ? "text-emerald-400" : "text-slate-400"}`} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-100">This is a long session — split into individual shots</p>
              <p className="text-xs text-slate-400 mt-0.5">The AI finds each delivery and reviews it as its own report.</p>
            </div>
            <div className={`h-5 w-9 rounded-full p-0.5 transition-colors ${sessionSplit ? "bg-emerald-500" : "bg-slate-700"}`}>
              <div className={`h-4 w-4 rounded-full bg-white transition-transform ${sessionSplit ? "translate-x-4" : ""}`} />
            </div>
          </div>
        </button>
      )}

      {/* Shared session fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase">Batter name</label>
          <Input
            data-testid="batter-name-input"
            value={batterName}
            onChange={(e) => setBatterName(e.target.value)}
            placeholder="e.g. Arjun Sharma"
            className="mt-1.5 bg-[#12181F] border-slate-700 text-slate-100 focus-visible:ring-emerald-500/40"
          />
        </div>
        <div>
          <label className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase">Handedness</label>
          <div className="mt-1.5 flex gap-2">
            {HANDS.map((h) => (
              <button
                key={h}
                data-testid={`hand-select-${h === "Right-Handed" ? "right" : "left"}`}
                onClick={() => setHand(h)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  hand === h ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-[#12181F] border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                {h === "Right-Handed" ? "Right" : "Left"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button
        data-testid="start-analysis-btn"
        onClick={submit}
        disabled={!clips.length || disabled}
        className="h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F12] font-heading font-bold text-base tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Video className="h-5 w-5 mr-2" />
        {clips.length === 1 && sessionSplit
          ? "Split session & analyze each shot"
          : multi
          ? mode === "multi_angle"
            ? `Analyze ${clips.length} angles as one shot`
            : `Analyze ${clips.length} shots`
          : "Run AI Assessment"}
      </Button>
    </div>
  );
};
